import { useEffect } from "react";

import { useRouter } from "next/router";

import * as Yup from "yup";

import { ComponentErrorBoundary } from "shared/components/atoms/ComponentErrorBoundary";
import { formatBrazilianCurrency } from "shared/utils/formatBrazilianCurrency";
import { useAuthContext } from "shared/utils/hooks/useAuthContext";
import { useGlobalContext } from "shared/utils/hooks/useGlobalContext";
import useWindowSize from "shared/utils/hooks/useWindowSize";
import openNotificationWithIcon from "shared/utils/openNotificationWithIcon";

import { IInstallment } from "@CheckoutStepsOffers/@types/offer";
import Button from "@CheckoutStepsOffers/components/atoms/Button";
import SectionHeader from "@CheckoutStepsOffers/components/templates/SectionHeader";
import useCheckoutStepsOffersContext from "@CheckoutStepsOffers/provider";
import { getPaymentSchemaValidation } from "@CheckoutStepsOffers/schema/getPaymentSchemaValidation";
import { getDocumentInformations } from "@CheckoutStepsOffers/utils/getDocumentInformations";
import { getUserNames } from "@CheckoutStepsOffers/utils/getUserNames";
import { handleSelectOrderBump } from "@Console/order_bump/utils/handleSelectOrderBump";
import { IOptionsProps } from "@CreateOfferModal/components/atoms/Select";
import OrderBumpCTA, {
  WrapperOrderBumpCTA,
} from "@payment/components/molecules/OrderBumpCTA";
import useCheckoutNavigationOffers from "@payment/hooks/useCheckoutNavigationOffers";
import usePayOffers, { IData } from "@payment/hooks/usePayOffers";
import { useCheckoutContext } from "@payment/providers/CheckoutProvider/useCheckoutContext";
import { generateEncryptedReference } from "@payment/utils/generateEncryptedReference";
import { getPaymentMethod } from "@payment/utils/getPaymentMethod";
import logToCheckoutMonitoring from "@payment/utils/logToCheckoutMonitoring";
import { savePaymentDetails } from "@payment/utils/savePaymentDetails";
import { captureException, captureMessage } from "@sentry/nextjs";
import { Scope, SubmitHandler } from "@unform/core";

import Coupon from "../ProductInfo/components/Coupon";
import DiscountInformation from "../ProductInfo/components/DiscountInformation";
import FinalPrices from "../ProductInfo/components/FinalPrices";
import FinalPricesOrderBump from "../ProductInfo/components/FinalPricesOrderBump";
import BillingInformationForm from "./components/BillingInformationForm";
import CreditCardForm from "./components/CreditCardForm";
import CreditCardList from "./components/CreditCardList";
import SelectPaymentMethods from "./components/SelectPaymentMethods";
import usePaymentContext, { PaymentProvider } from "./provider";
import {
  ChargeName,
  CreditCardAlert,
  Form,
  FormContainer,
  LoadingIcon,
  PaymentContainer,
  PaymentMobileContainer,
  SectionTitle,
} from "./styles";

const PaymentWithoutProvider = () => {
  const router = useRouter();

  const { width } = useWindowSize();

  const {
    offer,
    loader,
    coupon,
    setLoader,
    promoterLink,
    informations,
    methodPaymentSelected,
    methodPaymentSelectedInitialValue,
    userCanTrial,
    setUserCanTrial,

    orderBumpsSelected,
    orderBumpsInstallmentSelected,

    setOrderBumpsSelected,
    setOrderBumpsInstallmentSelected,
  } = useCheckoutStepsOffersContext();
  const { state } = useGlobalContext();
  const { loggedUser, signInCheckoutOnePage } = useAuthContext();
  const {
    setNewUserSettings,
    addressState,
    userSettingsState,
    country,
    countryId,
    clientIp,
  } = useCheckoutContext();
  const { formRef, openCreditCardForm } = usePaymentContext();

  const { addressList } = addressState;
  const { userSettings } = userSettingsState;
  const addressId = userSettings?.payment?.address_id;
  let dataForCheckoutMonitoring: any = "Sem dados";

  const [condition] = addressList?.filter((item: any) => item.id === addressId);
  const addressDefault: any =
    (condition && condition) || (addressList?.length >= 1 && addressList[0]);

  let errorDisplayed = false;

  const handleSubmit: SubmitHandler = async (data) => {
    setLoader(true);

    const schema = getPaymentSchemaValidation(data, country, offer);

    try {
      if (schema)
        await schema.validate(data, {
          abortEarly: false,
        });

      const names = getUserNames(informations.name);

      let documents = {};

      if (country === "Brasil") {
        documents = getDocumentInformations(informations.document);
      }

      let phone_number = informations.phone_number.replace(/[^+\d]+/g, "");
      if (!phone_number.startsWith("+")) {
        phone_number = "+" + phone_number;
      }

      const requestBody: IData = {
        ...names,
        ...documents,
        ...data?.address,
        ...data?.credit_card_list,
        ...data?.credit_card_form,

        country_id: countryId,

        offer_id: offer.offer.id,
        group_id: offer.group?.id,

        phone_number: phone_number,

        installments:
          Number(data?.credit_card_list?.installments) ||
          Number(data?.credit_card_form?.installments) ||
          1,

        clientIp,
      };

      if (!!orderBumpsSelected?.length)
        requestBody["order_bump"] = orderBumpsSelected?.map((orderBump) => ({
          id: orderBump?.id,
          installments:
            orderBumpsInstallmentSelected[orderBump?.slug]?.installment
              ?.installment,
        }));
      if (country !== "Brasil") requestBody["type"] = "individual";

      if (country !== "Brasil") requestBody["payment_method"] = "credit_card";
      else requestBody["payment_method"] = data.payment_method;

      if (coupon) requestBody["coupon"] = coupon.item.coupon_code;

      if (!!promoterLink) requestBody["promoter_link"] = promoterLink;

      if (!!informations?.email) {
        const emailInLowerCase = informations?.email?.toLowerCase();

        requestBody["email"] = emailInLowerCase;
        requestBody["email_confirmation"] = emailInLowerCase;
        requestBody["reference"] = generateEncryptedReference(
          emailInLowerCase,
          methodPaymentSelected,
        );
      } else
        requestBody["reference"] = generateEncryptedReference(
          loggedUser?.email?.toLowerCase(),
          methodPaymentSelected,
        );

      if (offer.offer.details.enable_default_address) {
        const addressKeys = [
          "street_number",
          "neighborhood",
          "zipcode",
          "street",
          "state",
          "city",
        ];

        Object.entries(offer.offer.details.default_address).forEach(
          ([key, value]) => {
            if (addressKeys.includes(key)) requestBody[key] = value;
          },
        );
      }

      if (!data?.address && !offer.offer.details.enable_default_address)
        requestBody["address_id"] = addressDefault?.id;

      if (!userCanTrial) requestBody["ignore_trial"] = true;

      dataForCheckoutMonitoring = requestBody;

      const response = await usePayOffers(requestBody);

      if (response.status !== 200) errorDisplayed = true;

      if (!loggedUser && !!response?.data?.token) {
        signInCheckoutOnePage({
          token: response.data?.token,
          user: response.data?.user,
        });
      }

      if (loggedUser || response.data.token)
        savePaymentDetails(requestBody, setNewUserSettings);

      if (
        (offer.offer.details.redirect_type === "custom" &&
          data?.payment_method === "credit_card") ||
        (offer.offer.details.redirect_type === "custom" &&
          coupon?.item?.price_with_discount === 0)
      )
        return window.open(offer.offer.details.redirect_url, "_self");

      const payload = {
        promoterLink: promoterLink,
        email: informations.email,
        requestBody,
        ...response?.data,
      };

      useCheckoutNavigationOffers(
        router,
        getPaymentMethod(data?.payment_method),
        payload,
      );
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const validationErrors = {};

        error.inner.forEach((error) => {
          validationErrors[error.path] = error.message;
        });

        formRef.current.setErrors(validationErrors);
        errorDisplayed = true;

        openNotificationWithIcon(
          "error",
          "Campos incorretos",
          "Preencha os campos corretamente antes de prosseguir",
        );

        console.table(error.inner);
      }

      if (
        error?.response?.status !== 200 &&
        error?.response?.status !== 500 &&
        error?.response !== undefined
      ) {
        const { errors, message, type } = error?.response?.data;

        const err = errors ? Object.values(errors) : message;

        logToCheckoutMonitoring(err, dataForCheckoutMonitoring);

        const errorsMessage = Array.isArray(err)
          ? err.map((error) => <li key={error}>{error[0]}</li>)
          : err?.message || err?.attrs?.message || err;

        errorDisplayed = true;

        openNotificationWithIcon(
          "error",
          "Erro ao processar pagamento",
          <ul>{errorsMessage}</ul>,
          6,
        );

        if (
          type === "warning" &&
          error?.response?.status === 400 &&
          message ===
            "Usuário com este e-mail não pode usar o período de avaliação para esta oferta."
        )
          errorDisplayed = true;

        setUserCanTrial(false);

        console.log(error);
      }

      if (!errorDisplayed) {
        openNotificationWithIcon(
          "error",
          "Não foi possível realizar esta compra no momento. Por favor, tente novamente mais tarde.",
        );
      }

      const errorMonitoringMessage = `PaymentWithoutProvider (handleSubmit): ${error}`;

      console.log(errorMonitoringMessage);
      captureException(error);
      captureMessage(errorMonitoringMessage);
    } finally {
      setLoader(false);
    }
  };

  const containers = {
    credit_card: openCreditCardForm ? <CreditCardForm /> : <CreditCardList />,
    boleto: <BillingInformationForm />,
    pix: <BillingInformationForm />,
  };

  const getAddressInitialData = () => {
    const { zipcode, street, street_number, neighborhood, city, state } =
      addressDefault;

    return {
      city,
      state,
      street,
      zipcode,
      neighborhood,
      street_number,
    };
  };

  const initialData = {
    payment_method: methodPaymentSelectedInitialValue,
    address: getAddressInitialData(),
  };

  const { installments } = useCheckoutStepsOffersContext();

  const lastInstallment = installments[installments?.length - 1];
  const textForInstallmentAmountVisualization = `${
    lastInstallment?.installment
  }x de ${formatBrazilianCurrency(lastInstallment?.value)}${
    lastInstallment?.free_installment ? " sem juros" : "*"
  }`;

  useEffect(
    () => () => {
      setOrderBumpsSelected([]);
      setOrderBumpsInstallmentSelected({});
    },
    [],
  );

  return (
    <Form ref={formRef} onSubmit={handleSubmit} initialData={initialData}>
      <PaymentContainer>
        <FormContainer>
          {country === "Brasil" ? (
            <SectionHeader
              title="Como você prefere pagar?"
              subtitle="Selecione um método de pagamento para prosseguir com a compra."
            />
          ) : (
            <SectionHeader title="Informações de pagamento" subtitle="" />
          )}

          {country === "Brasil" && <SelectPaymentMethods />}

          {containers[methodPaymentSelected]}

          {offer?.order_bump?.map((order_bump, index) => (
            <ComponentErrorBoundary key={order_bump?.id}>
              <WrapperOrderBumpCTA key={order_bump?.id}>
                <Scope path={`order_bump[${index}]`}>
                  <OrderBumpCTA
                    paymentMethodSelected={methodPaymentSelected}
                    orderBump={order_bump}
                    onSelectInstallments={(
                      option: IOptionsProps,
                      installment: IInstallment,
                    ) =>
                      setOrderBumpsInstallmentSelected((prevState) => ({
                        ...prevState,
                        [order_bump?.slug]: {
                          option,
                          installment,
                        },
                      }))
                    }
                    handleSelectOrderBump={() =>
                      handleSelectOrderBump(order_bump, setOrderBumpsSelected)
                    }
                  />
                </Scope>
              </WrapperOrderBumpCTA>
            </ComponentErrorBoundary>
          ))}

          {width > 750 && (
            <>
              <Button type="submit" loading={loader} disabled={loader}>
                {loader && <LoadingIcon />}
                {!!offer?.offer?.details?.trial_enabled ? (
                  <>
                    Experimente grátis por {offer?.offer?.details?.trial_days}{" "}
                    dias
                  </>
                ) : methodPaymentSelected === "pix" ? (
                  "Gerar Pix"
                ) : (
                  "Comprar"
                )}
              </Button>
              {methodPaymentSelected === "credit_card" && (
                <CreditCardAlert>
                  A cobrança aparecerá na sua fatura como:{" "}
                  <ChargeName>APP {state.tenancy.soft_descriptor}</ChargeName>
                </CreditCardAlert>
              )}
            </>
          )}
        </FormContainer>
      </PaymentContainer>

      {width <= 750 && (
        <PaymentMobileContainer>
          <SectionTitle>Resumo da compra</SectionTitle>

          <Coupon />

          {orderBumpsSelected?.length > 0 ? (
            <FinalPricesOrderBump
              textForInstallmentAmountVisualization={
                textForInstallmentAmountVisualization
              }
            />
          ) : (
            <FinalPrices
              textForInstallmentAmountVisualization={
                textForInstallmentAmountVisualization
              }
            />
          )}

          {width > 750 && <DiscountInformation />}

          <Button type="submit" loading={loader} disabled={loader}>
            {loader && <LoadingIcon />}

            {!!offer?.offer?.details?.trial_enabled ? (
              <>
                Experimente grátis por {offer?.offer?.details?.trial_days} dias
              </>
            ) : methodPaymentSelected === "pix" ? (
              "Gerar Pix"
            ) : (
              "Comprar"
            )}
          </Button>

          {methodPaymentSelected === "credit_card" && (
            <CreditCardAlert>
              A cobrança aparecerá na sua fatura como:{" "}
              <ChargeName>APP {state.tenancy.soft_descriptor}</ChargeName>
            </CreditCardAlert>
          )}
        </PaymentMobileContainer>
      )}
    </Form>
  );
};

const Payment = () => {
  return (
    <PaymentProvider>
      <PaymentWithoutProvider />
    </PaymentProvider>
  );
};

export default Payment;
